// Bvndle Partner Ecosystem snapshot — strategic-vs-long-tail view.
//
// Source: docs/bvndle_partner_ecosystem_2page.html (Data as of 12 May 2026).
// 25 active partners, 397 listed items, 13 categories, 5 pricing tiers.
//
// Ownership coded backend-side here (the ManCo lens). Defaults:
//   - "Williams"  — general merchants (Merchant List Run-through, daily)
//   - "Chibuzor"  — Yard Store + pop-ups + MTN Credit Vending
//   - "Ruki"      — BvndleGO (commercial bus + transport)
//   - "Ufuoma"    — redemption lead
//   - "Seye"      — LaaS / API-based issuer relationships (banks, Vertex3)
// Mapping is best-guess from the Action Tracker 20.5.26 sheet + Partner doc;
// adjust the `owner` field as ManCo reassigns. The `strategic` flag determines
// whether the partner surfaces on the SYM/D1 cards or in the Funnel long-tail
// panel; the cut today is "top-2 by item share + named anchor clients".

export interface PartnerRow {
  rank: number;
  name: string;
  category: string;
  /** Items listed on marketplace (12 May 2026 snapshot). */
  items: number;
  /** Share of total inventory, 0–1. */
  share: number;
  /** Bvndle ManCo owner of this partner relationship. */
  owner: string;
  /** True = surfaces on KPI cards (concentration narrative). False = funnel long-tail. */
  strategic: boolean;
}

export interface CategoryRow {
  name: string;
  items: number;
  coinRange: string;
  /** True for ManCo strategic-priority categories. */
  priority: boolean;
}

export interface TierRow {
  tier: "T1" | "T2" | "T3" | "T4" | "T5";
  coinRange: string;
  partners: number;
  items: number;
  share: number; // 0–1
  flag?: string; // e.g. "Peak", "Above MAU reach"
}

export interface ConcentrationFlag {
  label: string;
  value: string;
  tone: "warn" | "ok" | "neutral";
}

export interface PartnerEcosystemSnapshot {
  pulledAt: string;
  totals: {
    activePartners: number;
    listedItems: number;
    activeCategories: number;
    cheapestCoins: number;
    cheapestPartner: string;
    mostExpensiveCoins: number;
    mostExpensivePartner: string;
    peakInventoryMonth: { items: number; label: string };
  };
  tiers: TierRow[];
  categories: CategoryRow[];
  partners: PartnerRow[];
  flags: ConcentrationFlag[];
}

export const PARTNER_ECOSYSTEM: PartnerEcosystemSnapshot = {
  pulledAt: "2026-05-12",
  totals: {
    activePartners: 25,
    listedItems: 397,
    activeCategories: 13,
    cheapestCoins: 52,
    cheapestPartner: "Trolleys",
    mostExpensiveCoins: 430_283,
    mostExpensivePartner: "Geo Travels",
    peakInventoryMonth: { items: 103, label: "Mar 2026" },
  },
  tiers: [
    { tier: "T1", coinRange: "1 to 500",        partners: 4, items: 37,  share: 0.093 },
    { tier: "T2", coinRange: "501 to 2,000",    partners: 8, items: 129, share: 0.325 },
    { tier: "T3", coinRange: "2,001 to 10,000", partners: 7, items: 43,  share: 0.108 },
    { tier: "T4", coinRange: "10,001 to 50,000",partners: 5, items: 182, share: 0.458, flag: "Above MAU reach" },
    { tier: "T5", coinRange: "50,001+",         partners: 1, items: 6,   share: 0.015 },
  ],
  categories: [
    { name: "Luxury Retail",      items: 165, coinRange: "324 to 59,397",     priority: true  }, // Yard
    { name: "E-commerce",         items: 124, coinRange: "75 to 35,400",      priority: true  }, // GARA-MART
    { name: "Cosmetics & Beauty", items: 26,  coinRange: "1,100 to 25,000",   priority: true  },
    { name: "Groceries",          items: 21,  coinRange: "260 to 42,000",     priority: true  },
    { name: "Fashion",            items: 17,  coinRange: "2,800 to 23,500",   priority: true  },
    { name: "Travel & Transport", items: 10,  coinRange: "4,000 to 430,283",  priority: true  }, // BvndleGO + Geo
    { name: "Gifting",            items: 10,  coinRange: "300 fixed",         priority: true  }, // GTM lever
    { name: "Books",              items: 7,   coinRange: "1,500 to 20,000",   priority: false },
    { name: "Restaurants",        items: 5,   coinRange: "190 to 520",        priority: false },
    { name: "Electronics",        items: 5,   coinRange: "1,683 to 9,180",    priority: false },
    { name: "Retail",             items: 5,   coinRange: "52 to 580",         priority: false },
    { name: "Accessories",        items: 1,   coinRange: "930",               priority: false },
    { name: "Others",             items: 1,   coinRange: "17,500",            priority: false },
  ],
  partners: [
    { rank: 1,  name: "The Yard Store",       category: "Luxury Retail",     items: 165, share: 0.416, owner: "Chibuzor", strategic: true  },
    { rank: 2,  name: "GARA-MART LTD",        category: "E-commerce",        items: 91,  share: 0.229, owner: "Williams", strategic: true  },
    { rank: 3,  name: "pricepally nigeria",   category: "E-commerce",        items: 17,  share: 0.043, owner: "Williams", strategic: false },
    { rank: 4,  name: "scribbles.tle",        category: "Gifting",           items: 10,  share: 0.025, owner: "Williams · Seye (Gift Cards)", strategic: false },
    { rank: 5,  name: "s.plus integrated",    category: "Fashion",           items: 10,  share: 0.025, owner: "Williams", strategic: false },
    { rank: 6,  name: "laerelife",            category: "E-commerce",        items: 9,   share: 0.023, owner: "Williams", strategic: false },
    { rank: 7,  name: "Cellar Central",       category: "Groceries",         items: 8,   share: 0.020, owner: "Williams", strategic: false },
    { rank: 8,  name: "El Padrino",           category: "Groceries",         items: 8,   share: 0.020, owner: "Williams", strategic: false },
    { rank: 9,  name: "dang lifestyle ikeja", category: "Cosmetics",         items: 8,   share: 0.020, owner: "Williams", strategic: false },
    { rank: 10, name: "4ey Fashion",          category: "Fashion",           items: 7,   share: 0.018, owner: "Williams", strategic: false },
    { rank: 11, name: "geo travels",          category: "Travel & Transport",items: 6,   share: 0.015, owner: "Williams", strategic: false },
    { rank: 12, name: "rhela store",          category: "E-commerce",        items: 6,   share: 0.015, owner: "Williams", strategic: false },
    { rank: 13, name: "E BAR METRO",          category: "Groceries",         items: 6,   share: 0.015, owner: "Williams", strategic: false },
    { rank: 14, name: "cnm essentials",       category: "Cosmetics",         items: 6,   share: 0.015, owner: "Williams", strategic: false },
    { rank: 15, name: "the ace beauty bar",   category: "Cosmetics",         items: 6,   share: 0.015, owner: "Williams", strategic: false },
    { rank: 16, name: "the fragrance lagos",  category: "Cosmetics",         items: 5,   share: 0.013, owner: "Williams", strategic: false },
    { rank: 17, name: "purch gadgets",        category: "Electronics",       items: 5,   share: 0.013, owner: "Williams", strategic: false },
    { rank: 18, name: "trolleys superstore",  category: "Retail",            items: 5,   share: 0.013, owner: "Williams", strategic: false },
    { rank: 19, name: "ofada hut",            category: "Restaurants",       items: 5,   share: 0.013, owner: "Williams", strategic: false },
    { rank: 20, name: "BvndleGO",             category: "Travel & Transport",items: 4,   share: 0.010, owner: "Ruki", strategic: true  }, // strategic by client status, not item volume
    { rank: 21, name: "hot books",            category: "Books",             items: 4,   share: 0.010, owner: "Williams", strategic: false },
    { rank: 22, name: "Narrative Landscape",  category: "Books",             items: 2,   share: 0.005, owner: "Williams", strategic: false },
    { rank: 23, name: "split custodian",      category: "Accessories",       items: 2,   share: 0.005, owner: "Williams", strategic: false },
    { rank: 24, name: "dang orchid",          category: "Others",            items: 1,   share: 0.003, owner: "Williams", strategic: false },
    { rank: 25, name: "konga",                category: "E-commerce",        items: 1,   share: 0.003, owner: "Williams", strategic: false },
  ],
  flags: [
    { label: "Top partner by items",      value: "The Yard Store · 41.6%",            tone: "warn"    },
    { label: "Top 2 partners combined",   value: "Yard + GARA-MART · 64.5%",          tone: "warn"    },
    { label: "Largest category anchor",   value: "Luxury Retail · 1 partner (Yard)",  tone: "warn"    },
    { label: "Single-item partners",      value: "dang orchid, konga",                tone: "neutral" },
    { label: "Smallest categories",       value: "Accessories, Others · 1 item each", tone: "neutral" },
    { label: "Most accessible entry",     value: "52 BC · Trolleys",                  tone: "ok"      },
    { label: "Peak inventory month",      value: "Mar 2026 · 103 items",              tone: "ok"      },
    { label: "T4 share of inventory",     value: "45.8% above MAU reach",             tone: "warn"    },
    { label: "Dominant pricing bucket",   value: "10k to 50k BC · 91 items",          tone: "warn"    },
  ],
};

/** Long-tail partners — everything not flagged strategic. Rendered on the Funnel page. */
export const LONG_TAIL_PARTNERS = PARTNER_ECOSYSTEM.partners.filter((p) => !p.strategic);

/** Strategic anchor partners — surface on the SYM/D1 cards' concentration narrative. */
export const STRATEGIC_PARTNERS = PARTNER_ECOSYSTEM.partners.filter((p) => p.strategic);
